import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ArrowUpIcon,
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/solid';
import {
  AcademicCapIcon,
  BanknotesIcon,
  BoltIcon,
  BuildingStorefrontIcon,
  EllipsisHorizontalCircleIcon,
  FilmIcon,
  GiftIcon,
  GlobeAmericasIcon,
  HeartIcon,
  HomeModernIcon,
  ReceiptRefundIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  SparklesIcon,
  TagIcon,
  TvIcon,
  TruckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { categoriesAPI, transactionsAPI } from '../services/api';
import StatusToast from './StatusToast';
import SearchableSelect from './SearchableSelect';

const MAIN_CATEGORY_ICON_BY_NAME = {
  groceries: ShoppingCartIcon,
  'dining & restaurants': BuildingStorefrontIcon,
  transportation: TruckIcon,
  shopping: ShoppingBagIcon,
  entertainment: FilmIcon,
  'health & medical': HeartIcon,
  utilities: BoltIcon,
  housing: HomeModernIcon,
  insurance: ShieldCheckIcon,
  education: AcademicCapIcon,
  travel: GlobeAmericasIcon,
  subscriptions: TvIcon,
  'savings & investments': BanknotesIcon,
  'debt payments': ReceiptRefundIcon,
  'gifts & donations': GiftIcon,
  childcare: UserGroupIcon,
  pets: SparklesIcon,
  clothing: TagIcon,
  'personal services': ScissorsIcon,
  other: EllipsisHorizontalCircleIcon,
};

const getMainCategoryIcon = (name) => {
  const normalizedName = String(name || '').trim().toLowerCase();
  if (MAIN_CATEGORY_ICON_BY_NAME[normalizedName]) {
    return MAIN_CATEGORY_ICON_BY_NAME[normalizedName];
  }

  // Handle common custom/variant names so they don't all fall back to the same icon.
  if (normalizedName.includes('household') || normalizedName.includes('home')) return HomeModernIcon;
  if (normalizedName.includes('loan') || normalizedName.includes('debt')) return ReceiptRefundIcon;
  if (normalizedName.includes('subscription')) return TvIcon;
  if (normalizedName.includes('grocery')) return ShoppingCartIcon;
  if (normalizedName.includes('shop')) return ShoppingBagIcon;
  if (normalizedName.includes('travel')) return GlobeAmericasIcon;

  return TagIcon;
};

function CategoriesManager() {
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 768);
  const [showMobileAddButton, setShowMobileAddButton] = useState(() => window.scrollY > 120);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const [formData, setFormData] = useState({ name: '', color: '#3b82f6', parentCategoryId: '' });
  const [isCategoryNameFocused, setIsCategoryNameFocused] = useState(false);
  const [isCategoryNameSelectedFromSuggestion, setIsCategoryNameSelectedFromSuggestion] = useState(false);
  const [formErrors, setFormErrors] = useState({ name: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [newParentSubcategories, setNewParentSubcategories] = useState([]);
  const [newSubcategoryInput, setNewSubcategoryInput] = useState('');
  const [showAllCreateSubcategoryRefs, setShowAllCreateSubcategoryRefs] = useState(false);
  const [expandedParentIds, setExpandedParentIds] = useState([]);
  const [addingChildParentId, setAddingChildParentId] = useState(null);
  const [childFormData, setChildFormData] = useState({ name: '', color: '#3b82f6' });
  const [childFormError, setChildFormError] = useState('');
  const [savingChild, setSavingChild] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openParentActionMenuId, setOpenParentActionMenuId] = useState(null);
  const [mobileActionTarget, setMobileActionTarget] = useState(null);
  const lastScrollYRef = useRef(window.scrollY);
  const deleteMessageTimeoutRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleViewportUpdate = () => {
      setIsMobileView(window.innerWidth < 768);
      setShowMobileAddButton(window.scrollY > 120);
    };

    handleViewportUpdate();
    window.addEventListener('resize', handleViewportUpdate);
    window.addEventListener('scroll', handleViewportUpdate, { passive: true });
    return () => {
      window.removeEventListener('resize', handleViewportUpdate);
      window.removeEventListener('scroll', handleViewportUpdate);
    };
  }, []);

  useEffect(() => {
    const handleScrollDirection = () => {
      const currentScrollY = window.scrollY;
      const isScrollingUp = currentScrollY < lastScrollYRef.current;
      setShowScrollTopButton(currentScrollY > 240 && isScrollingUp);
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScrollDirection, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollDirection);
  }, []);

  useEffect(() => {
    return () => {
      if (deleteMessageTimeoutRef.current) {
        clearTimeout(deleteMessageTimeoutRef.current);
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesData, transactionsData] = await Promise.all([
        categoriesAPI.getAll(),
        transactionsAPI.getAll(),
      ]);
      setCategories(categoriesData);
      setTransactions(transactionsData);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const topLevelCategories = useMemo(
    () =>
      categories
        .filter((category) => !category.parentCategoryId)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [categories]
  );

  const childCategoriesByParentId = useMemo(() => {
    const map = categories
      .filter((category) => category.parentCategoryId)
      .reduce((acc, category) => {
        const parentId = String(category.parentCategoryId);
        if (!acc[parentId]) acc[parentId] = [];
        acc[parentId].push(category);
        return acc;
      }, {});

    Object.keys(map).forEach((parentId) => {
      map[parentId].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    });
    return map;
  }, [categories]);

  const usageCountByCategoryId = useMemo(() => {
    const usage = {};
    transactions.forEach((tx) => {
      const id = String(tx.categoryId || '');
      if (!id) return;
      usage[id] = (usage[id] || 0) + 1;
    });
    return usage;
  }, [transactions]);

  const filteredTopLevelCategories = useMemo(() => {
    const query = String(searchQuery || '').trim().toLowerCase();

    return topLevelCategories.filter((parent) => {
      const children = childCategoriesByParentId[String(parent.id)] || [];
      const parentName = String(parent.name || '').toLowerCase();
      const childMatch = children.some((child) => String(child.name || '').toLowerCase().includes(query));
      return !query || parentName.includes(query) || childMatch;
    });
  }, [topLevelCategories, childCategoriesByParentId, searchQuery]);

  const createCategoryNameSuggestions = useMemo(() => {
    if (!isAddingNew) {
      return [];
    }
    const query = String(formData.name || '').trim().toLowerCase();
    return topLevelCategories
      .filter((category) => !query || String(category.name || '').trim().toLowerCase().includes(query))
      .slice(0, 6);
  }, [topLevelCategories, formData.name, isAddingNew]);
  const matchedMainCategoryForCreate = useMemo(() => {
    if (!isAddingNew) {
      return null;
    }
    const query = String(formData.name || '').trim().toLowerCase();
    if (!query) {
      return null;
    }
    return topLevelCategories.find(
      (category) => String(category.name || '').trim().toLowerCase() === query
    ) || null;
  }, [isAddingNew, formData.name, topLevelCategories]);
  const existingCreateSubcategories = useMemo(
    () => (matchedMainCategoryForCreate ? (childCategoriesByParentId[String(matchedMainCategoryForCreate.id)] || []) : []),
    [matchedMainCategoryForCreate, childCategoriesByParentId]
  );
  const visibleCreateSubcategoryRefs = useMemo(
    () => (showAllCreateSubcategoryRefs ? existingCreateSubcategories : existingCreateSubcategories.slice(0, 5)),
    [showAllCreateSubcategoryRefs, existingCreateSubcategories]
  );
  const hiddenCreateSubcategoryRefCount = Math.max(existingCreateSubcategories.length - visibleCreateSubcategoryRefs.length, 0);
  const parentCategoryOptions = useMemo(
    () => ([
      { value: '', label: 'Top-level category' },
      ...topLevelCategories
        .filter((category) => category.id !== editingId)
        .map((category) => ({ value: String(category.id), label: category.name })),
    ]),
    [topLevelCategories, editingId]
  );

  const isDuplicateCategoryName = (name, parentCategoryId, excludeId = null) => {
    const normalizedName = String(name || '').trim().toLowerCase();
    const normalizedParentId = String(parentCategoryId || '');

    return categories.some((category) => {
      if (excludeId && String(category.id) === String(excludeId)) return false;
      return (
        String(category.parentCategoryId || '') === normalizedParentId &&
        String(category.name || '').trim().toLowerCase() === normalizedName
      );
    });
  };

  const validateMainForm = () => {
    const trimmedName = String(formData.name || '').trim();
    const targetParentId = isAddingNew ? '' : String(formData.parentCategoryId || '');
    const nextErrors = { name: '' };

    if (!trimmedName) {
      nextErrors.name = 'Category name is required.';
    } else if (!isAddingNew && isDuplicateCategoryName(trimmedName, targetParentId, editingId)) {
      nextErrors.name = 'A category with this name already exists in this level.';
    }

    setFormErrors(nextErrors);
    return !nextErrors.name;
  };

  const resetFormState = () => {
    setIsAddingNew(false);
    setEditingId(null);
    setIsCategoryNameFocused(false);
    setIsCategoryNameSelectedFromSuggestion(false);
    setFormData({ name: '', color: '#3b82f6', parentCategoryId: '' });
    setFormErrors({ name: '' });
    setNewParentSubcategories([]);
    setNewSubcategoryInput('');
    setShowAllCreateSubcategoryRefs(false);
    setChildFormError('');
  };

  const handleAddNew = () => {
    resetFormState();
    setIsAddingNew(true);
    setError('');
  };

  const handleEdit = (category) => {
    if (category?.isDefault) {
      setError('Default categories cannot be renamed.');
      return;
    }
    setEditingId(category.id);
    setIsAddingNew(false);
    setFormData({
      name: category.name,
      color: category.color,
      parentCategoryId: category.parentCategoryId || '',
    });
    setFormErrors({ name: '' });
    setNewParentSubcategories([]);
    setNewSubcategoryInput('');
    setShowAllCreateSubcategoryRefs(false);
    setChildFormError('');
    setError('');
  };

  const handleSave = async () => {
    if (!validateMainForm()) return;

    setSaving(true);
    setError('');
    try {
      if (isAddingNew) {
        const parentName = String(formData.name || '').trim();
        const normalizedParentName = parentName.toLowerCase();
        const childNames = [...new Set(
          [...newParentSubcategories, newSubcategoryInput].map((name) => String(name || '').trim()).filter(Boolean)
        )];
        const hasChildNames = childNames.length > 0;
        const existingMainCategory = categories.find(
          (category) =>
            !category.parentCategoryId &&
            String(category.name || '').trim().toLowerCase() === normalizedParentName
        );

        const created = [];
        let parentCategory = null;

        if (existingMainCategory) {
          if (!hasChildNames) {
            if (isCategoryNameSelectedFromSuggestion) {
              resetFormState();
              setExpandedParentIds((current) => (
                current.includes(existingMainCategory.id) ? current : [...current, existingMainCategory.id]
              ));
              setSaving(false);
              return;
            }
            setError(`Category "${existingMainCategory.name}" already exists.`);
            setSaving(false);
            return;
          }
          parentCategory = existingMainCategory;
        } else {
          parentCategory = await categoriesAPI.create({ ...formData, name: parentName, parentCategoryId: '' });
          created.push(parentCategory);
        }

        const parentId = String(parentCategory.id);
        const existingChildNames = new Set(
          categories
            .filter((category) => String(category.parentCategoryId || '') === parentId)
            .map((category) => String(category.name || '').trim().toLowerCase())
        );
        const missingChildNames = childNames.filter((name) => !existingChildNames.has(String(name).toLowerCase()));
        if (hasChildNames && existingMainCategory && missingChildNames.length === 0) {
          const subcategoryLabel = childNames.length === 1 ? 'subcategory' : 'subcategories';
          setError(
            `Main category "${parentCategory.name}" and ${subcategoryLabel} "${childNames.join(', ')}" already exist.`
          );
          return;
        }
        for (const childName of missingChildNames) {
          const childCategory = await categoriesAPI.create({
            name: childName,
            color: formData.color,
            parentCategoryId: parentId,
          });
          created.push(childCategory);
        }

        if (created.length > 0) {
          setCategories((current) => [...current, ...created]);
        }
        setExpandedParentIds((current) => (current.includes(parentCategory.id) ? current : [...current, parentCategory.id]));
      } else if (editingId) {
        const editingCategory = categories.find((cat) => String(cat.id) === String(editingId));
        if (editingCategory?.isDefault) {
          setError('Default categories cannot be renamed.');
          return;
        }
        const updatedCategory = await categoriesAPI.update(editingId, {
          ...formData,
          name: String(formData.name || '').trim(),
        });
        setCategories((current) => current.map((cat) => (cat.id === editingId ? updatedCategory : cat)));
      }
      resetFormState();
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.error || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    resetFormState();
    setError('');
  };

  const addCreateSubcategoryChip = (rawValue) => {
    const candidate = String(rawValue || '').trim();
    if (!candidate) {
      return;
    }
    const normalizedCandidate = String(candidate).toLowerCase();
    setNewParentSubcategories((current) => (
      current.some((entry) => String(entry || '').trim().toLowerCase() === normalizedCandidate)
        ? current
        : [...current, candidate]
    ));
  };

  const removeCreateSubcategoryChip = (chipName) => {
    const normalizedChip = String(chipName || '').trim().toLowerCase();
    setNewParentSubcategories((current) => (
      current.filter((entry) => String(entry || '').trim().toLowerCase() !== normalizedChip)
    ));
  };

  const toggleParentExpanded = (parentId) => {
    setOpenParentActionMenuId(null);
    setExpandedParentIds((current) =>
      current.includes(parentId) ? current.filter((id) => id !== parentId) : [...current, parentId]
    );
  };

  const openMobileActions = (category, kind) => {
    setMobileActionTarget({
      id: category.id,
      name: category.name,
      kind,
    });
  };

  const handleStartAddChild = (parentCategory) => {
    setAddingChildParentId(parentCategory.id);
    setChildFormData({ name: '', color: parentCategory.color || '#3b82f6' });
    setChildFormError('');
    setExpandedParentIds((current) => (current.includes(parentCategory.id) ? current : [...current, parentCategory.id]));
    setError('');
  };

  const handleCancelAddChild = () => {
    setAddingChildParentId(null);
    setChildFormData({ name: '', color: '#3b82f6' });
    setChildFormError('');
  };

  const handleSaveChild = async (parentCategory) => {
    const trimmedName = String(childFormData.name || '').trim();
    if (!trimmedName) {
      setChildFormError('Child category name is required.');
      return;
    }
    if (isDuplicateCategoryName(trimmedName, parentCategory.id)) {
      setChildFormError('A child category with this name already exists.');
      return;
    }

    setSavingChild(true);
    setChildFormError('');
    setError('');

    try {
      const createdChild = await categoriesAPI.create({
        name: trimmedName,
        color: childFormData.color || parentCategory.color || '#3b82f6',
        parentCategoryId: parentCategory.id,
      });
      setCategories((current) => [...current, createdChild]);
      setAddingChildParentId(null);
      setChildFormData({ name: '', color: '#3b82f6' });
    } catch (err) {
      console.error('Error creating child category:', err);
      setError(err.response?.data?.error || 'Failed to create child category');
    } finally {
      setSavingChild(false);
    }
  };

  const handleDelete = async (id) => {
    const setDeleteMessage = (message) => {
      setError(message);
      if (deleteMessageTimeoutRef.current) {
        clearTimeout(deleteMessageTimeoutRef.current);
      }
      deleteMessageTimeoutRef.current = setTimeout(() => {
        setError('');
      }, 5000);
    };

    const category = categories.find((c) => c.id === id);
    if (!category) return;

    if (category.isDefault) {
      setDeleteMessage('Cannot delete default categories');
      return;
    }

    const childCategories = childCategoriesByParentId[String(id)] || [];
    if (childCategories.length > 0) {
      setDeleteMessage(`"${category.name}" has ${childCategories.length} child categor${childCategories.length === 1 ? 'y' : 'ies'}. Remove or reassign them before deleting this parent category.`);
      return;
    }

    const usageCount = Number(usageCountByCategoryId[String(id)] || 0);
    if (usageCount > 0) {
      setDeleteMessage(`"${category.name}" is used in ${usageCount} transaction${usageCount === 1 ? '' : 's'}. Reassign those transactions before deleting this category.`);
      return;
    }

    const deletePrompt = `Delete category "${category.name}"?\n\nLinked transactions: ${usageCount}\nThis action cannot be undone.`;
    if (!window.confirm(deletePrompt)) return;

    try {
      await categoriesAPI.delete(id);
      setCategories((current) => current.filter((cat) => cat.id !== id));
    } catch (err) {
      console.error('Error deleting category:', err);
      setDeleteMessage(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleModalKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleCancel();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!saving) handleSave();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
          <p className="text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  const isCategoryEditorOpen = Boolean(isAddingNew || editingId);

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
        {!isAddingNew && !editingId && (
          <button onClick={handleAddNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
            + Add Category
          </button>
        )}
      </div>

      <div className="mb-5 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <label className="relative block md:w-80">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search categories or subcategories"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
        </div>
      </div>

      {isCategoryEditorOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" onKeyDown={handleModalKeyDown}>
          <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-5">
              <h3 className="text-lg font-medium text-gray-900">{isAddingNew ? 'Add New Category' : 'Edit Category'}</h3>
              <button type="button" onClick={handleCancel} className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50">
                Close
              </button>
            </div>
            <div className="space-y-3 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.name}
                    onFocus={() => setIsCategoryNameFocused(true)}
                    onBlur={() => setIsCategoryNameFocused(false)}
                    onChange={(event) => {
                      setIsCategoryNameSelectedFromSuggestion(false);
                      setShowAllCreateSubcategoryRefs(false);
                      setFormData({ ...formData, name: event.target.value });
                      if (formErrors.name) setFormErrors({ name: '' });
                    }}
                    placeholder={isAddingNew ? 'Type new main category or select existing' : 'e.g., Entertainment, Healthcare'}
                    className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 ${
                      formErrors.name ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  {isAddingNew && isCategoryNameFocused && createCategoryNameSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
                      {createCategoryNameSuggestions.map((category) => (
                        <button
                          key={`existing-main-manager-${category.id}`}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setIsCategoryNameSelectedFromSuggestion(true);
                            setShowAllCreateSubcategoryRefs(false);
                            setFormData((current) => ({ ...current, name: String(category.name || '') }));
                            setIsCategoryNameFocused(false);
                            if (formErrors.name) setFormErrors({ name: '' });
                          }}
                          className="w-full rounded-md px-2.5 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {formErrors.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
              </div>
              {isAddingNew && matchedMainCategoryForCreate && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Existing Subcategories</p>
                  {existingCreateSubcategories.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {visibleCreateSubcategoryRefs.map((subcategory) => (
                        <span key={`existing-subcategory-ref-${subcategory.id}`} className="rounded-full bg-white px-2 py-0.5 text-xs text-gray-700 border border-gray-200">
                          {subcategory.name}
                        </span>
                      ))}
                      {hiddenCreateSubcategoryRefCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowAllCreateSubcategoryRefs((current) => !current)}
                          className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100"
                        >
                          {showAllCreateSubcategoryRefs ? 'Show less' : `+${hiddenCreateSubcategoryRefCount} more`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">No subcategories yet.</p>
                  )}
                </div>
              )}

              {!isAddingNew && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
                  <SearchableSelect
                    value={String(formData.parentCategoryId || '')}
                    onChange={(selectedValue) => setFormData({ ...formData, parentCategoryId: selectedValue })}
                    options={parentCategoryOptions}
                    placeholder="Search parent category"
                    ariaLabel="Search parent category"
                    inputClassName="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    menuClassName="max-h-56"
                  />
                </div>
              )}
              {isAddingNew && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-2.5">
                  <p className="mb-2 text-sm font-medium text-blue-900">Optional Subcategories</p>
                  <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1.5">
                    {newParentSubcategories.map((name) => (
                      <span key={`subcategory-chip-manager-${name}`} className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                        {name}
                        <button
                          type="button"
                          onClick={() => removeCreateSubcategoryChip(name)}
                          className="text-blue-700 hover:text-blue-900"
                          aria-label={`Remove ${name}`}
                        >
                          x
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={newSubcategoryInput}
                      onChange={(event) => setNewSubcategoryInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ',') {
                          event.preventDefault();
                          addCreateSubcategoryChip(newSubcategoryInput);
                          setNewSubcategoryInput('');
                        }
                      }}
                      onBlur={() => {
                        addCreateSubcategoryChip(newSubcategoryInput);
                        setNewSubcategoryInput('');
                      }}
                      placeholder={newParentSubcategories.length > 0 ? 'Add another subcategory' : 'Type subcategory and press Enter'}
                      className="min-w-[180px] flex-1 border-0 px-1 py-1 text-sm focus:outline-none"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
              <button onClick={handleCancel} disabled={saving} className="text-sm font-medium text-gray-600 hover:text-gray-800 disabled:text-gray-400">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:bg-gray-400">
                {saving ? 'Saving...' : 'Save Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3 lg:max-h-[65vh] lg:overflow-y-auto lg:pr-1">
        {filteredTopLevelCategories.map((category) => {
          const childCategories = childCategoriesByParentId[String(category.id)] || [];
          const isExpanded = expandedParentIds.includes(category.id);
          const isAddingChild = addingChildParentId === category.id;
          const MainCategoryIcon = getMainCategoryIcon(category.name);

          return (
            <div key={category.id} className="relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md">
              {category.isDefault && (
                <span
                  className="absolute inset-y-3 left-0 w-1 rounded-r bg-blue-500"
                  title="System default category"
                  aria-label="System default category"
                />
              )}
              <div
                className="flex cursor-pointer items-center justify-between gap-3"
                onClick={() => toggleParentExpanded(category.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleParentExpanded(category.id);
                  }
                }}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.name}`}
              >
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 pr-2">
                  <div className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-gray-50" style={{ color: category.color }}>
                    <MainCategoryIcon className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <h3 className="truncate text-lg font-medium text-gray-900">{category.name}</h3>
                  <span className="whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{childCategories.length} subcat</span>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleParentExpanded(category.id);
                  }}
                  className="inline-flex items-center p-1 text-gray-700 hover:text-gray-900"
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Collapse category' : 'Expand category'}
                >
                  <ChevronDownIcon className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
              </div>

              <div className={`grid transition-all duration-300 ease-out ${isExpanded ? 'mt-4 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                  <div className="border-t border-gray-200 pt-3">
                    <div className="mb-2 flex items-center justify-end gap-2">
                      {!category.isDefault && (
                        <>
                          <div className="md:hidden">
                            <button
                              type="button"
                              onClick={() => openMobileActions(category, 'parent')}
                              className="inline-flex items-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                              aria-label="Category actions"
                            >
                              <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                          </div>
                          <div className="relative hidden md:block">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setOpenParentActionMenuId((current) => (current === category.id ? null : category.id));
                              }}
                              className="inline-flex items-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                              aria-label="Category actions"
                            >
                              <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            {openParentActionMenuId === category.id && (
                              <div className="absolute right-0 top-8 z-20 w-28 rounded-md border border-gray-200 bg-white p-1 shadow-lg">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenParentActionMenuId(null);
                                    handleEdit(category);
                                  }}
                                  className="w-full rounded px-2 py-1.5 text-left text-sm text-blue-600 hover:bg-blue-50"
                                  disabled={editingId === category.id}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenParentActionMenuId(null);
                                    handleDelete(category.id);
                                  }}
                                  className="w-full rounded px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Subcategories</p>
                      <button type="button" onClick={() => handleStartAddChild(category)} className="text-xs font-medium text-blue-700 hover:text-blue-800">+ Add Subcat</button>
                    </div>

                    {isAddingChild && (
                      <div className="mb-3 rounded border border-blue-200 bg-blue-50/40 p-2">
                        <input
                          type="text"
                          value={childFormData.name}
                          onChange={(event) => {
                            setChildFormData((current) => ({ ...current, name: event.target.value }));
                            if (childFormError) setChildFormError('');
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Escape') handleCancelAddChild();
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              if (!savingChild) handleSaveChild(category);
                            }
                          }}
                          placeholder="Subcategory name"
                          className={`w-full rounded px-2.5 py-1.5 text-sm ${childFormError ? 'border border-red-400 focus:outline-none focus:ring-2 focus:ring-red-400' : 'border border-gray-300'}`}
                        />
                        {childFormError && <p className="mt-1 text-xs text-red-600">{childFormError}</p>}
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <button type="button" onClick={handleCancelAddChild} className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100">Cancel</button>
                          <button type="button" onClick={() => handleSaveChild(category)} disabled={savingChild} className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700 disabled:bg-blue-400">{savingChild ? 'Saving...' : 'Save Child'}</button>
                        </div>
                      </div>
                    )}
                    {childCategories.length > 0 ? (
                      <div className="overflow-hidden rounded-lg border border-gray-200">
                        {childCategories.map((child) => (
                          <div key={child.id} className="group relative border-t border-gray-100 px-3 py-2">
                            {child.isDefault && (
                              <span
                                className="absolute inset-y-2 left-0 w-1 rounded-r bg-blue-500"
                                title="System default category"
                                aria-label="System default category"
                              />
                            )}
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-gray-800">
                                  {child.name}
                                  <span className="ml-2 text-xs font-normal text-gray-500">
                                    {Number(usageCountByCategoryId[String(child.id)] || 0)} tx
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {!child.isDefault && (
                                  <div className="hidden items-center gap-2 md:flex md:opacity-0 md:transition-opacity md:duration-150 md:group-hover:opacity-100">
                                    <button type="button" onClick={() => handleEdit(child)} className="text-xs text-blue-600 hover:text-blue-800" disabled={editingId === child.id}>Edit</button>
                                    <button type="button" onClick={() => handleDelete(child.id)} className="text-xs text-red-600 hover:text-red-800">Delete</button>
                                  </div>
                                )}
                                {!child.isDefault && (
                                  <div className="md:hidden">
                                    <button
                                      type="button"
                                      onClick={() => openMobileActions(child, 'child')}
                                      className="inline-flex items-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                      aria-label="Subcategory actions"
                                    >
                                      <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No child categories yet.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTopLevelCategories.length === 0 && categories.length > 0 && (
        <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
          <p className="text-sm text-gray-600">No categories match your current search or filter.</p>
        </div>
      )}

      {categories.length === 0 && !loading && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="mb-4 text-2xl font-semibold text-gray-500">No categories yet</div>
          <p className="text-sm text-gray-500">Click "Add Parent Category" to create your first category</p>
        </div>
      )}

      {mobileActionTarget && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/30 md:hidden"
            onClick={() => setMobileActionTarget(null)}
            aria-label="Close actions"
          />
          <div className="fixed inset-x-0 bottom-16 z-50 rounded-t-2xl border border-gray-200 bg-white p-4 pb-8 shadow-2xl md:hidden">
            <p className="mb-3 text-sm font-medium text-gray-700">
              {mobileActionTarget.kind === 'child' ? 'Subcategory' : 'Category'} actions
            </p>
            <p className="mb-4 truncate text-sm text-gray-500">{mobileActionTarget.name}</p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  const target = categories.find((entry) => String(entry.id) === String(mobileActionTarget.id));
                  setMobileActionTarget(null);
                  if (target) handleEdit(target);
                }}
                className="w-full rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-left text-sm font-medium text-blue-700"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  setMobileActionTarget(null);
                  handleDelete(mobileActionTarget.id);
                }}
                className="w-full rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-left text-sm font-medium text-red-700"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setMobileActionTarget(null)}
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-left text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
        <h4 className="mb-2 flex items-center gap-1 text-sm font-medium text-blue-900">
          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
          About Categories
        </h4>
        <ul className="space-y-1 text-sm text-blue-700">
          <li>- Default categories cannot be renamed or deleted</li>
          <li>- Custom categories can be edited or deleted</li>
          <li>- Category colors help you quickly identify transaction types</li>
          <li>- Each transaction must be assigned to one category</li>
        </ul>
      </div>

      {isMobileView && showMobileAddButton && !isAddingNew && !editingId && (
        <button
          type="button"
          onClick={handleAddNew}
          className="fixed bottom-20 right-4 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/30 hover:bg-blue-700"
          aria-label="Add category"
        >
          <PlusIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      )}

      {showScrollTopButton && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-24 left-1/2 z-20 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-gray-400/20 bg-gray-900/10 text-gray-700 hover:bg-gray-900/20"
          aria-label="Scroll to top"
        >
          <ArrowUpIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      <StatusToast
        error={error}
        onClearError={() => setError('')}
      />
    </div>
  );
}

export default CategoriesManager;
